import {useIsRTL, useTranslation} from '@aboutyou/i18n';
import type {TransitionComponentType} from '@aboutyou/transitions';
import React from 'react';
import styled, {css} from 'styled-components';
import {mobileOnly} from '../../../constants/customMedia';
import {AboutNameLogo} from './AdditionalLogos/AboutNameLogo/AboutNameLogo';
import {SpecialLogo, SpecialLogoOptions} from './AdditionalLogos/SpecialLogo';

export interface AboutYouLogoOptions {
  id?: string;
  firstName?: string;
  firstNameMaxLength?: number;
  disableSvgLogo: boolean;
  className?: string;
  testId?: string;
  disableLogoLink?: boolean;
  FirstNameAnimationComponent?: TransitionComponentType;
  showAYAnniversaryCampaign?: boolean;
  showHeartIcon?: boolean;
  trackOriginClickFunction?: VoidFunction;
}

type AboutYouLogoProps = SpecialLogoOptions & AboutYouLogoOptions;

export const AboutYouMainLogo: React.FC<AboutYouLogoProps> = ({
  firstName,
  firstNameMaxLength,
  isVip,
  woohooCampaign,
  disableSvgLogo,
  BASCampaignHeaderLogoComponent,
  className,
  testId,
  FirstNameAnimationComponent,
  disableLogoLink,
  showAYAnniversaryCampaign,
  showHeartIcon,
  trackOriginClickFunction,
  ...props
}) => {
  const {t} = useTranslation();
  const isRTL = useIsRTL();

  const hasAnySpecialLogo =
    isVip ||
    woohooCampaign?.isCampaignLive ||
    Boolean(BASCampaignHeaderLogoComponent);

  return (
    <Wrapper
      $isRTL={isRTL}
      $hasAnySpecialLogo={hasAnySpecialLogo}
      data-testid={testId ?? 'headerLogo'}
      aria-label={t('Start page')}
      className={className}
    >
      <StyledAboutNameLogo
        firstName={firstName}
        firstNameMaxLength={firstNameMaxLength}
        disableSvgLogo={disableSvgLogo}
        FirstNameAnimationComponent={FirstNameAnimationComponent}
        $isRTL={isRTL}
        trackOriginClickFunction={trackOriginClickFunction}
        disableLogoLink={disableLogoLink}
        showAYAnniversaryCampaign={showAYAnniversaryCampaign}
        showHeartIcon={showHeartIcon}
      />
      <StyledSpecialLogo
        $reducedSize={Boolean(isVip && showAYAnniversaryCampaign)}
        isVip={isVip}
        woohooCampaign={woohooCampaign}
        BASCampaignHeaderLogoComponent={BASCampaignHeaderLogoComponent}
        {...props}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div<{$isRTL: boolean; $hasAnySpecialLogo: boolean}>`
  display: flex;
  align-items: center;
  flex-direction: row;
  height: 35px;
  padding: 0px 5px 0px 4px;

  ${mobileOnly(css`
    flex-grow: 1;
  `)}

  ${({$isRTL, $hasAnySpecialLogo}) =>
    $isRTL &&
    $hasAnySpecialLogo &&
    css`
      direction: initial;
    `}
`;

const StyledAboutNameLogo = styled(AboutNameLogo)<{
  $isRTL: boolean;
}>`
  ${({$isRTL}) =>
    $isRTL
      ? css`
          margin-left: 1px;
        `
      : css`
          margin-right: 1px;
        `}
`;

const StyledSpecialLogo = styled(SpecialLogo)<{$reducedSize: boolean}>`
  ${({$reducedSize}) =>
    $reducedSize &&
    css`
      ${mobileOnly(css`
        svg {
          width: 22px;
        }
      `)}
    `}
`;
